import React, { useState, useEffect } from "react";
import { Container, Row, Col, Table, Form, Button, Spinner, Alert } from "react-bootstrap";
import { FaEdit, FaTrash, FaPlus, FaEye, FaTruck, FaRedo } from "react-icons/fa";
import { Link } from "react-router-dom";
import { usePagination } from "../hooks/usePagination";
import Pagination from "../Components/Pagination";
import api from "../services/api";
import "./CustomerList.css";

const DeliveryNoteList = () => {
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [refresh, setRefresh] = useState(0);

  // Status class function
  const getStatusClass = (status) => {
    const s = String(status || "");
    if (s === "0") return "status-badge status-progress";
    if (s === "2" ) return "status-badge status-pending";
    if (s === "1" ) return "status-badge status-completed";
    // if (s === "cancelled" || s === "rejected") return "status-badge status-rejected";
    // if (s === "shipped") return "status-badge status-approved";
    return "status-badge status-default";
  };

  // Fetch delivery notes from API
  const fetchDeliveryNotes = async () => {
    try {
      setLoading(true);
      setError("");
      const response = await api.post("/delivery_note_list.php", {});
      
      if (response.data.head.code === 200) {
        setDeliveryNotes(response.data.body);
      } else {
        setError(response.data.head.msg || "Failed to fetch delivery notes");
        setDeliveryNotes([]);
      }
    } catch (err) {
      console.error("Error fetching delivery notes:", err);
      setError("Failed to load delivery notes. Please try again.");
      setDeliveryNotes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDeliveryNotes();
  }, [refresh]);

  // Delete delivery note handler
  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to delete this delivery note?")) {
      try {
        const response = await api.post("/delivery_note_delete.php", { id });
        
        if (response.data.head.code === 200) {
          alert("Delivery note deleted successfully!");
          fetchDeliveryNotes(); // Refresh the list
        } else {
          alert("Failed to delete delivery note: " + response.data.head.msg);
        }
      } catch (err) {
        console.error("Error deleting delivery note:", err);
        alert("Failed to delete delivery note. Please try again.");
      }
    }
  };

  const parseYYYYMMDD = (dateStr) => {
  if (!dateStr) return null;

  const [year, month, day] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

const parseDDMMYYYY = (dateStr) => {
  if (!dateStr) return null;

  const [day, month, year] = dateStr.split("-");
  return new Date(year, month - 1, day);
};

  // Filter delivery notes based on search, date range, and status
  const filteredDeliveryNotes = deliveryNotes.filter((note) => {
    if (!search && !fromDate && !toDate && !statusFilter) return true;
    
    const query = search.toLowerCase();
    const matchesSearch = 
      String(note.id).toLowerCase().includes(query) ||
      // (note.invoice_no && note.invoice_no.toLowerCase().includes(query)) ||
      (note.customer_name && note.customer_name.toLowerCase().includes(query)) 
      // (note.customer_phone && note.customer_phone.includes(query)) ||
      // (note.driver_name && note.driver_name.toLowerCase().includes(query)) ||
      // (note.driver_phone && note.driver_phone.includes(query)) ||
      // (note.vehicle_no && note.vehicle_no.toLowerCase().includes(query))
      ;

    // Date range filtering
    // const noteDate = new Date(note.date);
    // const from = fromDate ? new Date(fromDate) : null;
    // const to = toDate ? new Date(toDate) : null;
    const noteDate = parseDDMMYYYY(note.date);
    const from = fromDate ? parseYYYYMMDD(fromDate) : null;
    const to = toDate ? parseYYYYMMDD(toDate) : null;

    const matchesDateRange = 
      (!from || noteDate >= from) && 
      (!to || noteDate <= to);

    // Status filtering
    const matchesStatus = !statusFilter || note.status === statusFilter;

    return matchesSearch && matchesDateRange && matchesStatus;
  });

  // Calculate stats based on FILTERED notes
  const deliveredCount = filteredDeliveryNotes.filter(dn => dn.status === 'Delivered').length;
  const transitCount = filteredDeliveryNotes.filter(dn => dn.status === 'In Transit').length;
  const pendingCount = filteredDeliveryNotes.filter(dn => dn.status != 1).length;
  const completedCount = filteredDeliveryNotes.filter(dn => dn.status == 1).length;
  const cancelledCount = filteredDeliveryNotes.filter(dn => dn.status === 'Cancelled').length;

  // Pagination hook
  const {
    currentPage,
    totalPages,
    currentItems: currentDeliveryNotes,
    handlePageChange,
    indexOfFirstItem
  } = usePagination(filteredDeliveryNotes, 10);

  const clearDateFilter = () => {
    setFromDate("");
    setToDate("");
  };

  const clearStatusFilter = () => {
    setStatusFilter("");
  };

  const clearAllFilters = () => {
    setFromDate("");
    setToDate("");
    setStatusFilter("");
    setSearch("");
  };

  const refreshList = () => {
    setRefresh(prev => prev + 1);
  };

  return (
    <Container fluid className="outer-container my-3">
      <h2 className="text-center mb-3">Delivery Note Management</h2>
      
      {/* Error Alert */}
      {error && (
        <Alert variant="danger" className="mb-3">
          {error}
          <Button variant="outline-danger" size="sm" className="ms-3" onClick={refreshList}>
            <FaRedo /> Retry
          </Button>
        </Alert>
      )}

      {/* Filters Section */}
      <div className="filters-section mb-4 p-3 border rounded bg-light">
        <Row className="g-3">
          {/* Date Range Filters */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>From Date:</Form.Label>
              <Form.Control
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>To Date:</Form.Label>
              <Form.Control
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                disabled={loading}
              />
            </Form.Group>
          </Col>
          
          {/* Status Filter */}
          <Col xs={12} md={6} lg={3}>
            <Form.Group>
              <Form.Label>Status:</Form.Label>
              <Form.Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={loading}
              >
                <option value="">All Status</option>
                <option value="0">Pending</option>
                {/* <option value="In Transit">In Transit</option>
                <option value="Delivered">Delivered</option> */}
                <option value="1">Completed</option>
              </Form.Select>
            </Form.Group>
          </Col>

          {/* Filter Actions */}
          <Col xs={12} md={6} lg={3} className="d-flex align-items-end gap-2">
            <Button 
              variant="outline-secondary" 
              onClick={clearDateFilter}
              disabled={!fromDate && !toDate || loading}
              size="sm"
            >
              Clear Dates
            </Button>
            <Button 
              variant="outline-secondary" 
              onClick={clearAllFilters}
              disabled={!fromDate && !toDate && !statusFilter && !search || loading}
              size="sm"
            >
              Clear All
            </Button>
            {/* <Button 
              variant="outline-primary" 
              onClick={refreshList}
              size="sm"
              title="Refresh"
              disabled={loading}
            >
              <FaRedo />
            </Button> */}
          </Col>
        </Row>
      </div>

      {/* Search and Add Delivery Note Section */}
      <Row className="mb-3 align-items-center">
        <Col xs={12} md={6} lg={4} className="mb-2 mb-md-0">
          <Form.Control
            placeholder="Search by DN No, customer name"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            disabled={loading}
          />
        </Col>

        <Col xs={12} md={6} lg={8} className="d-flex justify-content-md-end gap-2">
          {/* <Button 
            variant="outline-secondary" 
            onClick={refreshList}
            disabled={loading}
          >
            <FaRedo /> Refresh
          </Button> */}
          <Link
            to="/adddeliverynote"
            className="btn-top d-inline-flex align-items-center"
          >
            <FaPlus className="me-2" /> Add Delivery Note
          </Link>
        </Col>
      </Row>

      {/* Stats Summary - Now shows FILTERED counts */}
      <Row className="mb-4">
        <Col xs={6} md={4}>
          <div className="stat-card text-center p-3 border rounded bg-warning text-dark">
            <h4 className="mb-1 me-2">{pendingCount}</h4>
            <small>Pending</small>
          </div>
        </Col>
         <Col xs={6} md={4}>
          <div className="stat-card text-center p-3 border rounded bg-success text-white">
            <h4 className="mb-1 me-2">{completedCount}</h4>
            <small>Completed</small>
          </div>
        </Col>
        {/*
        <Col xs={6} md={4}>
          <div className="stat-card text-center p-3 border rounded bg-info text-white">
            <h4 className="mb-1 me-2">{completedCount}</h4>
            <small>Completed</small>
          </div>
        </Col> 
        */}
        
       
        <Col xs={6} md={4}>
          <div className="stat-card text-center p-3 border rounded bg-primary text-white">
            <h4 className="mb-1 me-2">{filteredDeliveryNotes.length}</h4>
            <small>Total DN</small>
          </div>
        </Col>
      </Row>

      {/* Loading Spinner */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">Loading delivery notes...</p>
        </div>
      ) : (
        <>
          {/* Table Section */}
          <div className="table-pagination-wrapper">
            <div className="table-pagination-content">
              <Table bordered responsive className="table-striped customer-table">
                <thead>
                  <tr>
                    <th>S.No</th>
                    <th>DN Number</th>
                    <th>Date</th>
                    <th>Customer Name</th>
                    <th>Driver Name</th>
                    <th>Driver Phone</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentDeliveryNotes.map((note, index) => (
                    <tr key={note.id}>
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        <div className="d-flex align-items-center">
                          <FaTruck className="text-primary me-2" />
                          {note.id}
                        </div>
                      </td>
                      <td>{note.date}</td>
                      <td>
                        <div>
                          <div>{note.customer_name || "N/A"}</div>
                          <small className="text-muted">{note.customer_phone || ""}</small>
                        </div>
                      </td>
                      <td>{note.driver_name || "N/A"}</td>
                      <td>{note.driver_phone || "N/A"}</td>
                      <td>
                        <span className={getStatusClass(note.status)}>
                          {/* {note.status || "Pending"} */}
                          {note.status == 0 ? "Pending" : note.status == 1 ? "Completed" : "Processing"}
                        </span>
                      </td>
                      <td>
                        <div className="action-icons">
                          <Link
                            to={`/viewdeliverynote/${note.id}`}
                            className="view-link me-2"
                            title="View Delivery Note"
                          >
                            <FaEye className="view-icon" />
                          </Link>
                          {/* <Link
                            to={`/editdeliverynote/${note.id}`}
                            className="me-2"
                            title="Edit Delivery Note"
                          >
                            <FaEdit className="edit-icon" />
                          </Link> */}
                          <FaTrash
                            className="delete-icon"
                            title="Delete Delivery Note"
                            onClick={() => handleDelete(note.id)}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      </td>
                    </tr>
                  ))}
                  {currentDeliveryNotes.length === 0 && !loading && (
                    <tr>
                      <td colSpan="9" className="text-center py-4">
                        <div className="text-muted">
                          <FaTruck size={48} className="mb-3" />
                          <p>No delivery notes found</p>
                          <small>Try adjusting your search or filters</small>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </div>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          )}

          {/* Summary Footer - Removed Total Items */}
          <Row className="mt-4">
            <Col xs={12}>
              <div className="summary-footer p-3 border rounded bg-light">
                <Row>
                  <Col md={4}>
                    <strong>Total Delivery Notes:</strong> {filteredDeliveryNotes.length}
                  </Col>
                  <Col md={4}>
                    <strong>Pending:</strong> {pendingCount}
                  </Col>
                  <Col md={4}>
                    <strong>Completed:</strong> {completedCount}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </>
      )}
    </Container>
  );
};

export default DeliveryNoteList;